import type { DataTokenType, TemplateAutocomplete } from '@onefootprint/request-types/dashboard';
import type { Bundle, BundleNode, Field, FieldType } from './types';
import { isDecisionNode, isEnumType, isRulesNode, isTaskNode } from './types';

const FIELD_TYPE_TO_DATA_TOKEN: Record<FieldType, DataTokenType> = {
  text: 'string',
  document: 'document',
  number: 'number',
  integer: 'integer',
  boolean: 'boolean',
  enum: 'string',
  object: 'object',
  'array<string>': 'array',
  'array<enum>': 'array',
  'array<object>': 'array',
};

const supportedValuesFor = (field: Pick<Field, 'enumValues'> & { type: FieldType | null }): string[] | undefined => {
  if (isEnumType(field.type)) return field.enumValues;
  if (field.type === 'boolean') return ['true', 'false'];
  return undefined;
};

// Scalar fields are addressed as `outputs.<key>` in CEL — the older `.value`
// selector is rejected by the BE on scalar schemas as a "legacy shape".
//
// Only nodes strictly before `beforeNodeId` contribute tokens — the BE resolves
// field references against earlier nodes only, so anything else fails validation.
export const tokensFromBundle = (bundle: Bundle, beforeNodeId: string): TemplateAutocomplete => {
  const inputTokens = bundle.inputs.map(input => {
    const path = `inputs.${input.name}`;
    const dataType: DataTokenType = input.type === null ? 'unknown' : FIELD_TYPE_TO_DATA_TOKEN[input.type];
    return {
      display: path,
      token: path,
      dataType: [dataType],
      supportedValues: supportedValuesFor(input),
    };
  });

  const earlierNodes: BundleNode[] = [];
  for (const node of bundle.nodes) {
    if (node.id === beforeNodeId) break;
    earlierNodes.push(node);
  }
  const fieldTokens = earlierNodes.filter(isTaskNode).flatMap(t =>
    t.fields.map(f => {
      const path = `outputs.${f.name}`;
      return {
        display: path,
        token: path,
        dataType: [FIELD_TYPE_TO_DATA_TOKEN[f.type]],
        supportedValues: supportedValuesFor(f),
      };
    }),
  );
  const rulesTokens = earlierNodes.filter(isRulesNode).map(r => {
    const path = `outputs.${r.key}`;
    const values = [r.defaultValue, ...r.groups.map(g => g.value)].map(v => v.trim()).filter(v => v.length > 0);
    return {
      display: path,
      token: path,
      dataType: ['string'] as [DataTokenType],
      supportedValues: values.length > 0 ? [...new Set(values)] : undefined,
    };
  });
  const decisionTokens = earlierNodes.filter(isDecisionNode).flatMap(node =>
    node.questions.map(question => {
      const path = `outputs.${question.key}`;
      if (question.kind === 'yes_no') {
        return { display: path, token: path, dataType: ['boolean'] as [DataTokenType], supportedValues: undefined };
      }
      if (question.kind === 'score') {
        return { display: path, token: path, dataType: ['number'] as [DataTokenType], supportedValues: undefined };
      }
      const values = question.options.map(option => option.value.trim()).filter(value => value.length > 0);
      return {
        display: path,
        token: path,
        dataType: ['string'] as [DataTokenType],
        supportedValues: values.length > 0 ? [...new Set(values)] : undefined,
      };
    }),
  );
  return { tokens: [...inputTokens, ...fieldTokens, ...rulesTokens, ...decisionTokens] };
};
